import React from 'react';
import PropTypes from 'prop-types';
import styles from './StatCard.module.css';

/**
 * Sleek, modern StatCard component for displaying statistical information
 *
 * Supports multiple layouts and variants:
 * - Layout: 'vertical' (default) or 'horizontal'
 * - Variant: 'default', 'primary', 'success', 'warning', 'error', 'secondary'
 *
 * Examples:
 *
 * Simple vertical card:
 * <StatCard number="142" label="Total Users" detail="32 active" />
 *
 * Horizontal card with React icon (e.g., Lucide):
 * <StatCard
 *   number="24"
 *   label="Pending Reviews"
 *   icon={<Clock size={24} />}
 *   variant="warning"
 *   layout="horizontal"
 * />
 *
 * With Font Awesome icon (backwards compatibility):
 * <StatCard
 *   number="24"
 *   label="Pending Reviews"
 *   icon="fas fa-clock"
 *   variant="warning"
 * />
 *
 * With loading state:
 * <StatCard number="--" label="Loading..." loading />
 */
const StatCard = ({
  number,
  label,
  detail,
  icon,
  variant = 'default',
  layout = 'vertical',
  loading = false,
  className = '',
  onClick,
  href,
  ...rest
}) => {
  const Component = href ? 'a' : onClick ? 'button' : 'div';

  const classNames = [
    styles.statCard,
    styles[`variant-${variant}`],
    styles[`layout-${layout}`],
    loading && styles.loading,
    (onClick || href) && styles.interactive,
    className
  ].filter(Boolean).join(' ');

  // Support both React icon components and Font Awesome icon strings
  const renderIcon = () => {
    if (!icon) return null;
    
    // If icon is a string (Font Awesome), render as class name
    if (typeof icon === 'string') {
      return <i className={icon}></i>;
    }
    
    // If icon is a React element, render it directly
    return icon;
  };

  const content = (
    <>
      {icon && (
        <div className={styles.iconContainer}>
          {renderIcon()}
        </div>
      )}
      <div className={styles.content}>
        <div className={styles.number}>
          {loading ? (
            <div className={styles.skeleton}></div>
          ) : (
            number
          )}
        </div>
        <div className={styles.label}>{label}</div>
        {detail && <div className={styles.detail}>{detail}</div>}
      </div>
    </>
  );

  const props = {
    className: classNames,
    ...(href && { href }),
    ...(onClick && { onClick, type: 'button' }),
    ...rest
  };

  return <Component {...props}>{content}</Component>;
};

StatCard.propTypes = {
  /** The main statistic number to display */
  number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

  /** The label describing the statistic */
  label: PropTypes.string.isRequired,

  /** Optional detail text (e.g., "32 active") */
  detail: PropTypes.string,

  /** Optional icon - can be a Font Awesome icon class string (e.g., "fas fa-users") or a React icon component (e.g., <Users size={24} />) */
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /** Visual variant of the card */
  variant: PropTypes.oneOf(['default', 'primary', 'success', 'warning', 'error', 'secondary']),

  /** Layout direction */
  layout: PropTypes.oneOf(['vertical', 'horizontal']),

  /** Show loading skeleton */
  loading: PropTypes.bool,

  /** Additional CSS class names */
  className: PropTypes.string,

  /** Click handler (makes card interactive) */
  onClick: PropTypes.func,

  /** Link URL (renders as anchor tag) */
  href: PropTypes.string,
};

export default StatCard;
